import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_BASE } from '../../config/api';
import { 
  Upload, 
  Image as ImageIcon, 
  Trash2, 
  Search, 
  Grid, 
  List,
  Copy,
  Check,
  X,
  Filter,
  Loader2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
const API = `${API_BASE}/api`;

const CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'thumbnail', label: 'Thumbnails' },
  { value: 'course', label: 'Course Images' },
  { value: 'infographic', label: 'Infographics' },
  { value: 'video', label: 'Videos' },
  { value: 'ebook', label: 'eBook Covers' },
  { value: 'banner', label: 'Banners' },
];

// File size limits
const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB for images
const MAX_VIDEO_SIZE = 500 * 1024 * 1024; // 500MB for videos
const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks for large files

export function MediaLibraryAdmin({ token, onSelectImage }) {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const fetchMedia = useCallback(async () => {
    try {
      setLoading(true);
      // Use the existing /media endpoint
      const response = await axios.get(`${API}/media`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Handle array response from /media endpoint
      const mediaList = Array.isArray(response.data) ? response.data : (response.data.media || []);
      // Filter by category if selected
      const filtered = selectedCategory 
        ? mediaList.filter(m => (m.category || m.type) === selectedCategory)
        : mediaList;
      setMedia(filtered);
    } catch (error) {
      console.error('Failed to fetch media:', error);
      toast.error('Failed to load media library');
    } finally {
      setLoading(false);
    }
  }, [token, selectedCategory]);

  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  const [uploadProgress, setUploadProgress] = useState({});

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploading(true);
    
    for (const file of files) {
      const isVideo = file.type.startsWith('video/');
      const maxSize = isVideo ? MAX_VIDEO_SIZE : MAX_IMAGE_SIZE;
      
      if (file.size > maxSize) {
        toast.error(`${file.name} is too large. Max size: ${isVideo ? '500MB' : '10MB'}`);
        continue;
      }
      
      // Use chunked upload for files larger than 5MB
      if (file.size > CHUNK_SIZE) {
        await uploadLargeFile(file);
      } else {
        await uploadSmallFile(file);
      }
    }
    
    setUploading(false);
    setUploadProgress({});
    fetchMedia();
    e.target.value = '';
  };

  // Standard upload for smaller files
  const uploadSmallFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    // Determine file type category
    let fileType = selectedCategory || 'general';
    if (file.type.startsWith('video/')) {
      fileType = 'video';
    } else if (file.type.startsWith('image/')) {
      if (!selectedCategory || selectedCategory === 'general') {
        fileType = 'image';
      }
    }
    formData.append('file_type', fileType);
    
    try {
      await axios.post(`${API}/media/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(prev => ({ ...prev, [file.name]: percent }));
        }
      });
      toast.success(`Uploaded: ${file.name}`);
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(`Failed to upload ${file.name}: ${error.response?.data?.detail || 'Unknown error'}`);
    }
  };

  // Chunked upload for large files (videos, infographics)
  const uploadLargeFile = async (file) => {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const uploadId = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Determine file type
    let fileType = selectedCategory || 'general';
    if (file.type.startsWith('video/')) {
      fileType = 'video';
    } else if (file.type.startsWith('image/')) {
      fileType = 'image';
    }
    
    try {
      // Upload chunks
      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        const start = chunkIndex * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        
        const formData = new FormData();
        formData.append('file', chunk);
        formData.append('upload_id', uploadId);
        formData.append('chunk_index', chunkIndex.toString());
        formData.append('total_chunks', totalChunks.toString());
        formData.append('original_filename', file.name);
        formData.append('file_type', fileType);
        formData.append('content_type', file.type);
        formData.append('total_size', file.size.toString());
        
        await axios.post(`${API}/media/upload-chunk`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        // Update progress
        const percent = Math.round(((chunkIndex + 1) / totalChunks) * 100);
        setUploadProgress(prev => ({ ...prev, [file.name]: percent }));
      }
      
      toast.success(`Uploaded: ${file.name}`);
    } catch (error) {
      console.error('Chunked upload failed:', error);
      toast.error(`Failed to upload ${file.name}: ${error.response?.data?.detail || 'Upload failed'}`);
    }
  };

  const handleDelete = async (mediaId) => {
    if (!window.confirm('Are you sure you want to delete this media?')) return;
    
    try {
      // Use existing /media endpoint
      await axios.delete(`${API}/media/${mediaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Image deleted');
      fetchMedia();
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete image');
    }
  };

  const copyUrl = (url) => {
    const fullUrl = `${API}${url}`;
    navigator.clipboard.writeText(fullUrl);
    setCopiedId(url);
    toast.success('URL copied to clipboard');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const filteredMedia = media.filter(item => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const filename = item.original_filename || item.filename || '';
    return (
      filename.toLowerCase().includes(query) ||
      item.tags?.some(tag => tag.toLowerCase().includes(query))
    );
  });

  const formatFileSize = (bytes) => {
    const size = bytes || 0;
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };
  
  // Handle both old and new media format
  const getMediaUrl = (item) => {
    const url = item.url || '';
    // If it's already a full URL starting with http, use as-is
    if (url.startsWith('http')) {
      return url;
    }
    // If it's a relative path starting with /api, prepend the backend URL and encode spaces
    if (url.startsWith('/api/')) {
      // Encode special characters in the path (spaces, commas, etc.)
      const encodedPath = url.split('/').map((part, index) => 
        index > 3 ? encodeURIComponent(part) : part
      ).join('/');
      return `${API_BASE}${encodedPath}`;
    }
    // Fallback: prepend API base URL
    return `${API}${url}`;
  };
  
  const getMediaId = (item) => item.media_id || item.id;
  const getFilename = (item) => item.original_filename || item.filename;
  const getFileSize = (item) => item.size_bytes || item.size || 0;
  const getCategory = (item) => item.category || item.type || 'general';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Media Library</h2>
          <p className="text-zinc-400 mt-1">
            Upload and manage images, videos, and infographics for courses
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <input
            type="file"
            id="media-upload"
            multiple
            accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime,video/x-msvideo"
            className="hidden"
            onChange={handleUpload}
          />
          <label 
            htmlFor="media-upload"
            className="flex items-center gap-2 px-4 py-2 bg-navigate-gold text-black rounded-lg hover:bg-navigate-gold-light cursor-pointer transition-colors"
          >
            {uploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            Upload Media
          </label>
        </div>
      </div>

      {/* Upload Progress */}
      {Object.keys(uploadProgress).length > 0 && (
        <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-4 space-y-3">
          <h3 className="text-sm font-medium text-white">Uploading...</h3>
          {Object.entries(uploadProgress).map(([filename, percent]) => (
            <div key={filename} className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="text-zinc-400 truncate max-w-[200px]">{filename}</span>
                <span className="text-zinc-300">{percent}%</span>
              </div>
              <div className="w-full bg-zinc-700 rounded-full h-2">
                <div 
                  className="bg-navigate-gold h-2 rounded-full transition-all duration-300"
                  style={{ width: `${percent}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filters & Search */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input
            placeholder="Search by filename or tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-zinc-800 border-zinc-700 text-white"
          />
        </div>
        
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-zinc-400" />
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="bg-zinc-800 border border-zinc-700 text-white rounded-lg px-3 py-2 text-sm"
          >
            <option value="">All Categories</option>
            {CATEGORIES.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
        </div>
        
        <div className="flex items-center gap-1 bg-zinc-800 rounded-lg p-1">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded ${viewMode === 'grid' ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:text-white'}`}
          >
            <Grid className="h-4 w-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded ${viewMode === 'list' ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:text-white'}`}
          >
            <List className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-6 text-sm text-zinc-400">
        <span>{filteredMedia.length} images</span>
        <span>
          Total size: {formatFileSize(filteredMedia.reduce((sum, m) => sum + getFileSize(m), 0))}
        </span>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-navigate-gold" />
        </div>
      ) : filteredMedia.length === 0 ? (
        <div className="text-center py-20 bg-zinc-800/30 rounded-xl border border-dashed border-zinc-700">
          <ImageIcon className="h-16 w-16 text-zinc-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No Images Yet</h3>
          <p className="text-zinc-400 mb-4">Upload images to build your media library</p>
          <label 
            htmlFor="media-upload"
            className="inline-flex items-center gap-2 px-4 py-2 bg-navigate-gold text-black rounded-lg hover:bg-navigate-gold-light cursor-pointer transition-colors"
          >
            <Upload className="h-4 w-4" />
            Upload Your First Image
          </label>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {filteredMedia.map((item) => (
            <div 
              key={getMediaId(item)}
              className={`group relative bg-zinc-800 rounded-xl overflow-hidden border-2 transition-all cursor-pointer ${
                selectedMedia && getMediaId(selectedMedia) === getMediaId(item) 
                  ? 'border-navigate-gold' 
                  : 'border-transparent hover:border-zinc-600'
              }`}
              onClick={() => {
                setSelectedMedia(item);
                if (onSelectImage) {
                  onSelectImage(item);
                }
              }}
            >
              <div className="aspect-square">
                <img
                  src={getMediaUrl(item)}
                  alt={getFilename(item)}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    copyUrl(item.url);
                  }}
                  className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
                  title="Copy URL"
                >
                  {copiedId === item.url ? (
                    <Check className="h-4 w-4 text-green-400" />
                  ) : (
                    <Copy className="h-4 w-4 text-white" />
                  )}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(getMediaId(item));
                  }}
                  className="p-2 bg-red-500/20 rounded-lg hover:bg-red-500/40 transition-colors"
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4 text-red-400" />
                </button>
              </div>
              
              {/* Info */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                <p className="text-xs text-white truncate">{getFilename(item)}</p>
                <p className="text-xs text-zinc-400">{formatFileSize(getFileSize(item))}</p>
              </div>
              
              {/* Category Badge */}
              <div className="absolute top-2 left-2">
                <span className="text-xs bg-black/50 text-white px-2 py-0.5 rounded capitalize">
                  {getCategory(item)}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {filteredMedia.map((item) => (
            <div 
              key={getMediaId(item)}
              className={`flex items-center gap-4 p-3 bg-zinc-800 rounded-lg border-2 transition-all cursor-pointer ${
                selectedMedia && getMediaId(selectedMedia) === getMediaId(item) 
                  ? 'border-navigate-gold' 
                  : 'border-transparent hover:border-zinc-600'
              }`}
              onClick={() => {
                setSelectedMedia(item);
                if (onSelectImage) {
                  onSelectImage(item);
                }
              }}
            >
              <div className="w-16 h-16 rounded-lg overflow-hidden bg-zinc-700 shrink-0">
                <img
                  src={getMediaUrl(item)}
                  alt={getFilename(item)}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate">{getFilename(item)}</p>
                <div className="flex items-center gap-3 text-xs text-zinc-400 mt-1">
                  <span className="capitalize">{getCategory(item)}</span>
                  <span>{formatFileSize(getFileSize(item))}</span>
                  <span>{new Date(item.created_at).toLocaleDateString()}</span>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    copyUrl(item.url);
                  }}
                  className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg transition-colors"
                  title="Copy URL"
                >
                  {copiedId === item.url ? (
                    <Check className="h-4 w-4 text-green-400" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(getMediaId(item));
                  }}
                  className="p-2 text-zinc-400 hover:text-red-400 hover:bg-zinc-700 rounded-lg transition-colors"
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Selected Image Preview Modal */}
      {selectedMedia && !onSelectImage && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setSelectedMedia(null)}>
          <div className="bg-zinc-900 rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-zinc-800">
              <h3 className="text-lg font-semibold text-white">{selectedMedia.original_filename}</h3>
              <button onClick={() => setSelectedMedia(null)} className="p-1 hover:bg-zinc-800 rounded">
                <X className="h-5 w-5 text-zinc-400" />
              </button>
            </div>
            <div className="p-4">
              <img
                src={`${API}${selectedMedia.url}`}
                alt={selectedMedia.original_filename}
                className="max-w-full max-h-[60vh] mx-auto rounded-lg"
              />
            </div>
            <div className="p-4 border-t border-zinc-800 space-y-2">
              <div className="flex items-center gap-4 text-sm">
                <span className="text-zinc-400">Size:</span>
                <span className="text-white">{formatFileSize(selectedMedia.size_bytes)}</span>
                <span className="text-zinc-400">Category:</span>
                <span className="text-white capitalize">{selectedMedia.category}</span>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={`${API}${selectedMedia.url}`}
                  className="bg-zinc-800 border-zinc-700 text-white text-sm"
                />
                <Button onClick={() => copyUrl(selectedMedia.url)} variant="outline" size="sm">
                  {copiedId === selectedMedia.url ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MediaLibraryAdmin;
